import '@Pages/GeoMMM/GeoMMM.scss';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { GeoMMMInfoRow } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import { Intent, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Page } from '@Components/Page/Page';
import {
  geoKpiKey,
  geoToModelRuns,
  markRunsWithoutScenarioModel
} from '@Pages/MMM/MmmRunSelector/mmm-run-selector-adapters';
import { MmmRunSelector } from '@Pages/MMM/MmmRunSelector/MmmRunSelector';
import { Results } from '@Pages/GeoMMM/Results/Results';
import { FAQ } from '@Pages/MMM/FAQ/FAQ';
import { useFeatureMapping } from '@Pages/MMM/hooks/useFeatureMapping';
import { ScenarioPlanning } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanning';
import {
  DMA_MAP_DEMO_COMPANY,
  DEMO_DMAS_TO_INCLUDE,
  MODEL_BUCKET
} from '@Pages/GeoMMM/geo-mmm-constants';
import {
  downloadGeoMmmWorkbook,
  isTransientGeoMmmLoadError,
  probeGeoMmmRuns
} from '@Pages/GeoMMM/geo-mmm-load-utils';
import { getVisibleModelRuns } from '@Pages/GeoMMM/geo-mmm-visible-runs';
import { cleanWorkbookSheetRows } from '@Pages/GeoMMM/geo-mmm-adstock';
import {
  GeoMmmSheets,
  isMeridianSource,
  loadGeoMmmMeridianSheets
} from '@Pages/GeoMMM/geo-mmm-meridian-loader';
import { useMmmPage } from '@Pages/MMM/use-mmm-page';
import {
  isNaSegment,
  isRawFeatureId,
  lookupFeatureIdentity,
  orderedSlugs
} from '../../utils/mmm-feature-mapping';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useSetError } from '../../redux/modals';
import { awaitJSON, MetricsLambdaFetch } from '../../utils/fetch-utils';

const TabKey = {
  RESULTS: 'results',
  FAQ: 'faq',
  SCENARIO_PLANNING: 'scenario-planning'
} as const;
type TabKey = (typeof TabKey)[keyof typeof TabKey];

const SHEET_NAMES = [
  'Summary Metrics',
  'MAPE by Geo',
  'Fit',
  'Media Spend',
  'Attribution Over Time',
  'ROI Table', // Includes info from Marginal ROI
  'Response Curves',
  'Adstock',
  'Attribution by Geo',
  'Grouping'
];

// Newer model runs' Excel workbooks (generated by `meridian`) don't always resolve `nice_names`,
// so the `channel` columns in Grouping/ROI Table/Response Curves/Adstock/Attribution by
// Geo/Attribution Over Time (and the Media Spend column headers) can carry raw opaque feature ids
// (e.g. `featid1007`) instead of pretty names. Scenario Planning already solves this exact problem
// for its own (JSON) data via
// `isRawFeatureId`/`applyFeatureMappingToRow` in mmm-feature-mapping.ts; these helpers reuse the
// same primitives (raw-id detection, the feature-id map, and its `keys`-ordered slugs) for the
// Excel-sourced sheets here. GeoMMM's Grouping sheet carries 4 separate breakout columns
// (channel/platform/tactic/brand_nonbrand) rather than the 3-slot channel/platform/tactic fold
// `resolveFeatureParts` uses for Scenario Planning's UI (which has no dedicated Brand/Non-Brand
// column), so these read the feature identity's fields directly instead.

// Decomposes a raw feature id into GeoMMM's 4 breakout dimensions, for the Grouping sheet where
// each dimension is its own column. Returns null when the value isn't a raw id or the map has no
// entry, so callers keep the row's existing (already human-readable) values untouched.
export const resolveGeoMmmBreakoutFields = (
  value: string | null | undefined,
  map?: MmmFeatureNameResponse
): { channel: string; platform: string; tactic: string; brand_nonbrand: string } | null => {
  if (!map || !value || !isRawFeatureId(value)) return null;
  const identity = lookupFeatureIdentity(value, map);
  if (!identity) return null;
  const slugs = orderedSlugs(map);
  // Blank out N/A segments (not just missing ones) so the composite key this feeds into
  // (channelBreakoutMap, joined via `.filter(Boolean)`) matches the descriptor
  // resolveFeatureIdToBreakoutDescriptor produces for the same feature id elsewhere — that
  // helper drops N/A segments outright rather than joining the literal string "N/A".
  const valueAt = (index: number): string => {
    const slug = slugs[index];
    const fieldValue = slug ? identity.fields[slug] : undefined;
    return fieldValue && !isNaSegment(fieldValue) ? fieldValue : '';
  };
  return {
    channel: valueAt(0),
    platform: valueAt(1),
    tactic: valueAt(2),
    brand_nonbrand: valueAt(3)
  };
};

// Resolves a raw feature id to the same lowercased, comma-joined breakout descriptor the Grouping
// sheet's channelBreakoutMap keys use (e.g. "linear tv, nbc, national, brand"), for sheets
// (ROI Table, Response Curves, Adstock, Attribution by Geo, and Media Spend's column headers) that
// carry only a single opaque `channel` token representing every dimension at once. Non-raw values
// pass through unchanged so already-readable rows are never rewritten.
export const resolveFeatureIdToBreakoutDescriptor = (
  value: string,
  map?: MmmFeatureNameResponse
): string => {
  if (!map || !isRawFeatureId(value)) return value;
  const identity = lookupFeatureIdentity(value, map);
  if (!identity) return value;
  const parts = orderedSlugs(map)
    .map(slug => identity.fields[slug])
    .filter(fieldValue => Boolean(fieldValue) && !isNaSegment(fieldValue))
    .map(fieldValue => fieldValue.toLowerCase())
    .join(', ');
  return parts || value;
};

const loadGeoMmmWorkbookSheets = async (source: string): Promise<GeoMmmSheets> => {
  const arrayBuffer = await downloadGeoMmmWorkbook(
    source.startsWith(MODEL_BUCKET) ? source : `${MODEL_BUCKET}/${source}`
  );
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  // Mirror SheetJS sheet_to_json: row 1 is the header, each subsequent row becomes an
  // object keyed by header. Unwrap formula/rich-text cells to their plain value.
  const getCellValue = (value: ExcelJS.CellValue): unknown => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
      if ('result' in value) return value.result ?? null;
      if ('richText' in value) return value.richText.map(rt => rt.text).join('');
      if ('text' in value) return value.text;
    }
    return value;
  };
  const sheetJsons: GeoMmmSheets = {};
  SHEET_NAMES.forEach(name => {
    const worksheet = workbook.getWorksheet(name);
    if (!worksheet) {
      throw new Error(`Sheet "${name}" not found`);
    }
    const headers: string[] = [];
    worksheet.getRow(1).eachCell((cell, colNumber) => {
      headers[colNumber] = String(cell.value ?? '');
    });
    const rows: Record<string, unknown>[] = [];
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const obj: Record<string, unknown> = {};
      headers.forEach((header, colNumber) => {
        if (!header) return;
        obj[header] = getCellValue(row.getCell(colNumber).value);
      });
      rows.push(obj);
    });
    sheetJsons[name] = cleanWorkbookSheetRows(name, rows);
  });
  return sheetJsons;
};

export const GeoMMM = () => {
  const { tab, goToTab, navs, isInternal } = useMmmPage({
    baseURL: 'geo-mmm',
    navs: [
      { key: TabKey.RESULTS, label: 'Model Results' },
      { key: TabKey.SCENARIO_PLANNING, label: 'Scenario Planning', requiresInternal: true },
      { key: TabKey.FAQ, label: 'FAQ' }
    ]
  });
  const { cid: company } = useCompanyInfo();
  const { data: featureMapping } = useFeatureMapping(company ?? '', 'GeoMMM');
  const [currentModelRun, setCurrentModelRun] = useState<GeoMMMInfoRow>();
  const [groupBy, setGroupBy] = useState('channel');
  const [modelRunOptions, setModelRunOptions] = useState<GeoMMMInfoRow[]>();
  const [geoMMDataMap, setGeoMMMDataMap] = useState<Record<string, any>>({});
  const [isInitialLoaded, setIsInitialLoaded] = useState<boolean>(false);
  const [loadAttempt, setLoadAttempt] = useState<number>(0);
  const setError = useSetError();

  const kpi = useMemo(() => currentModelRun?.kpi, [currentModelRun]);
  const kpiRuns = useMemo(() => geoToModelRuns(modelRunOptions ?? []), [modelRunOptions]);
  // On the Scenario Planning tab the same picker marks runs that have no scenario model.
  const selectorKpiRuns = useMemo(
    () =>
      tab === TabKey.SCENARIO_PLANNING
        ? markRunsWithoutScenarioModel(kpiRuns, run =>
            (modelRunOptions ?? []).some(
              model =>
                geoKpiKey(model) === run.kpi &&
                model.modelRunDate === run.refreshDate &&
                !!model.scenarioPlanning
            )
          )
        : kpiRuns,
    [tab, kpiRuns, modelRunOptions]
  );
  const { modelRunDate, outcomeVariable, source } = useMemo(
    () => ({
      modelRunDate: currentModelRun?.modelRunDate,
      outcomeVariable: currentModelRun?.outcome as 'revenue' | 'kpiVolume',
      source: currentModelRun?.source
    }),
    [currentModelRun]
  );

  const dataKey = useMemo(() => source, [source]);

  // The page's selected run and its scenario model (carried on the run row): one selector drives both tabs.
  const sharedRun = useMemo(
    () => (currentModelRun ? { model: currentModelRun.scenarioPlanning } : undefined),
    [currentModelRun]
  );
  const runModels = useMemo(
    () =>
      (modelRunOptions ?? []).flatMap(row => (row.scenarioPlanning ? [row.scenarioPlanning] : [])),
    [modelRunOptions]
  );

  const geoMMMData = useMemo(() => geoMMDataMap[`${dataKey}`], [dataKey, geoMMDataMap]);

  useEffect(() => {
    if (company && !modelRunOptions) {
      (async () => {
        try {
          const res = await MetricsLambdaFetch('/getGeoMMMInfo', {
            params: {
              company
            }
          });
          const parsedRes = await awaitJSON<GeoMMMInfoRow[]>(res);
          if (!parsedRes.length) {
            throw new Error(`No model runs found for ${company}`);
          }
          // Verify each model's data file exists in S3 before offering it, so runs whose
          // artifact is missing are never surfaced. A probe that cannot get an answer keeps the
          // run, so a network blip never hides it.
          const existsResults = await probeGeoMmmRuns(
            parsedRes.map(model =>
              model.source.startsWith(MODEL_BUCKET)
                ? model.source
                : `${MODEL_BUCKET}/${model.source}`
            )
          );
          const existingModels = parsedRes.filter((_, i) => existsResults[i]);
          if (!existingModels.length) {
            throw new Error(`No model runs with available data found for ${company}`);
          }
          const visibleModels = getVisibleModelRuns(existingModels, isInternal);
          if (!visibleModels.length) {
            throw new Error(`No client-facing model runs found for ${company}`);
          }
          setModelRunOptions(visibleModels);
          setCurrentModelRun(visibleModels[0]);
        } catch (e) {
          setError({
            message: e.message,
            reportError: e
          });
        }
      })();
    }
  }, [company, isInternal, modelRunOptions, setError]);

  useEffect(() => {
    // Keyed on whether this run was attempted (a failed load stores undefined), so a failure
    // doesn't refetch in a loop.
    if (
      dataKey &&
      modelRunOptions &&
      currentModelRun &&
      company &&
      !Object.hasOwn(geoMMDataMap, dataKey)
    ) {
      (async () => {
        try {
          const sheetJsons = isMeridianSource(currentModelRun.source)
            ? await loadGeoMmmMeridianSheets(company, currentModelRun.source)
            : await loadGeoMmmWorkbookSheets(currentModelRun.source);
          setGeoMMMDataMap(prev => ({ ...prev, [dataKey]: sheetJsons }));
          setIsInitialLoaded(true);
        } catch (e) {
          // Surface the real cause — the initial-load path below silently swallows it otherwise.
          console.error('Failed to load/parse GeoMMM model data:', e);
          if (isTransientGeoMmmLoadError(e)) {
            // A network error says nothing about the run, so keep it selectable instead of
            // dropping it and loading another workbook. Dismissing the dialog retries the load.
            setError({
              message: e.message,
              reportError: e,
              onOkay: () => setLoadAttempt(n => n + 1)
            });
            return;
          }
          if (isInitialLoaded) {
            setError({
              message: e.message,
              reportError: e
            });
          }
          setGeoMMMDataMap(prev => ({ ...prev, [dataKey]: undefined }));
          // Drop the failed run so it can't be re-selected into a spinner that never loads.
          setModelRunOptions(prev => prev?.filter(modelRun => modelRun.source !== dataKey));
          // Fall back to a run that already loaded, else to one not yet attempted.
          const existingModel = R.find(
            modelRun => !R.isNil(geoMMDataMap[modelRun.source]),
            modelRunOptions
          );
          if (existingModel) {
            setCurrentModelRun(existingModel);
            setIsInitialLoaded(true);
          } else {
            let areAnyModelsLeft = false;
            // Search for a new model to load
            for (const modelRun of modelRunOptions) {
              if (modelRun.source !== dataKey && !Object.hasOwn(geoMMDataMap, modelRun.source)) {
                setCurrentModelRun(modelRun);
                areAnyModelsLeft = true;
                break;
              }
            }
            if (!areAnyModelsLeft) {
              setError({
                message: e.message,
                reportError: e
              });
            }
          }
        }
      })();
    }
  }, [
    company,
    currentModelRun,
    dataKey,
    geoMMDataMap,
    isInitialLoaded,
    kpi,
    loadAttempt,
    modelRunOptions,
    outcomeVariable,
    setError
  ]);

  const [geoNames, setGeoNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (R.isEmpty(R.keys(geoNames))) {
      (async () => {
        try {
          const dmaData = await fetch('https://cdn.blisspointmedia.com/geo/DmaPaths.json');
          const dmaPaths = await dmaData.json();

          const dmaNamesMap: Record<string, string> = {};
          R.keys(dmaPaths).forEach(dma => {
            dmaNamesMap[String(dma)] = dmaPaths[dma].name;
          });
          setGeoNames(dmaNamesMap);
        } catch (e) {
          setError({
            message: `Failed to get dma path information: ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [geoNames, setError]);

  const {
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
    channelBreakoutMap
  } = useMemo(() => {
    // Resolve raw featids to pretty names before anything downstream reads channel/platform/tactic,
    // so both the Grouping-derived channelBreakoutMap and every sheet that keys into it (or reads
    // its own `channel` column directly, e.g. NextMediaDollar's snapshot chart) see the same
    // resolved values. See the comment on the resolve* helpers above for why Grouping (4 separate
    // columns) and the single-`channel`-column sheets need different resolution shapes.
    const channelBreakoutRows: any[] = (
      geoMMMData?.Grouping.filter((row: any) => row.channel !== 'All Channels') || []
    ).map((row: any) => {
      const resolved = resolveGeoMmmBreakoutFields(row.channel, featureMapping);
      return resolved ? { ...row, ...resolved } : row;
    });
    const mapping: Record<
      string,
      { channel: string; platform: string; tactic: string; brand_nonbrand: string }
    > = {};
    if (!R.isEmpty(channelBreakoutRows)) {
      channelBreakoutRows.forEach(entry => {
        const channel = entry.channel?.toLowerCase();
        const platform = entry.platform?.toLowerCase();
        const tactic = entry.tactic?.toLowerCase();
        const brand_nonbrand = entry.brand_nonbrand?.toLowerCase();
        const parts = [channel, platform, tactic, brand_nonbrand].filter(Boolean).join(', ');

        mapping[parts] = {
          channel,
          platform,
          tactic,
          brand_nonbrand
        };
      });
    }

    // Sheets that carry a single opaque `channel` column standing in for every dimension at once —
    // resolved to the same composite descriptor used as channelBreakoutMap's keys above.
    const resolveChannelColumnRows = (rows: any[]): any[] =>
      rows.map(row => ({
        ...row,
        channel: resolveFeatureIdToBreakoutDescriptor(row.channel, featureMapping)
      }));

    // Media Spend has one column PER channel, so the featids appear as object keys, not values.
    const resolveMediaSpendHeaders = (rows: any[]): any[] =>
      rows.map(row => {
        const resolvedRow: Record<string, unknown> = {};
        Object.keys(row).forEach(key => {
          const resolvedKey = isRawFeatureId(key)
            ? resolveFeatureIdToBreakoutDescriptor(key, featureMapping)
            : key;
          resolvedRow[resolvedKey] = row[key];
        });
        return resolvedRow;
      });

    return {
      summaryMetricsRows: geoMMMData ? geoMMMData['Summary Metrics'] : [],
      mapeByGeoRows: geoMMMData ? geoMMMData['MAPE by Geo'] : [],
      fitRows: geoMMMData ? geoMMMData.Fit : [],
      mediaSpendRows: geoMMMData
        ? resolveMediaSpendHeaders(
            geoMMMData['Media Spend'].filter((row: any) => row.channel !== 'All Channels')
          )
        : [],
      attributionOverTimeRows: geoMMMData
        ? resolveChannelColumnRows(geoMMMData['Attribution Over Time'])
        : [],
      roiTableRows: geoMMMData
        ? resolveChannelColumnRows(
            geoMMMData['ROI Table'].filter((row: any) => row.channel !== 'All Channels')
          )
        : [],
      responseCurvesRows: geoMMMData
        ? resolveChannelColumnRows(
            geoMMMData['Response Curves'].filter((row: any) => row.channel !== 'All Channels')
          )
        : [],
      adstockRows: geoMMMData
        ? resolveChannelColumnRows(
            geoMMMData.Adstock.filter((row: any) => row.channel !== 'All Channels')
          )
        : [],
      attributionByGeoRows: geoMMMData
        ? resolveChannelColumnRows(
            geoMMMData['Attribution by Geo'].filter((row: any) => row.channel !== 'All Channels')
          )
        : [],
      channelBreakoutMap: mapping
    };
  }, [geoMMMData, featureMapping]);

  // Group By options derived from the Grouping sheet (channelBreakoutMap): a dimension is offered
  // only if some breakout actually has it.
  const groupByOptions = useMemo<{ label: string; value: string }[]>(() => {
    const breakouts = Object.values(channelBreakoutMap);
    const hasPlatform = breakouts.some(breakout => Boolean(breakout.platform));
    const hasTactic = breakouts.some(breakout => Boolean(breakout.tactic));
    const hasBrand = breakouts.some(breakout => Boolean(breakout.brand_nonbrand));

    const options = [{ label: 'Channel', value: 'channel' }];
    if (hasPlatform) {
      options.push({ label: 'Channel, Platform', value: 'platform' });
    }
    if (hasTactic || hasBrand) {
      const labelParts = ['Channel'];
      if (hasPlatform) {
        labelParts.push('Platform');
      }
      if (hasTactic) {
        labelParts.push('Tactic');
      }
      if (hasBrand) {
        labelParts.push('Brand/Nonbrand');
      }
      options.push({ label: labelParts.join(', '), value: 'name' });
    }
    return options;
  }, [channelBreakoutMap]);

  // If the active grouping is no longer offered (e.g. after switching model runs), reset to Channel.
  useEffect(() => {
    if (!groupByOptions.some(option => option.value === groupBy)) {
      setGroupBy('channel');
    }
  }, [groupByOptions, groupBy]);

  // Workbook response curves are whole-window totals, so the chart divides them by the run's week
  // count; Meridian response curves are already weekly.
  const responseCurveDivisor = useMemo<number | undefined>(
    () =>
      isMeridianSource(source)
        ? 1
        : summaryMetricsRows?.find((row: any) => row.metric === 'Length of Time Series')?.value,
    [source, summaryMetricsRows]
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  useEffect(() => {
    if (
      outcomeVariable &&
      !R.isEmpty(summaryMetricsRows) &&
      !R.isEmpty(mapeByGeoRows) &&
      !R.isEmpty(fitRows) &&
      !R.isEmpty(mediaSpendRows) &&
      !R.isEmpty(attributionOverTimeRows) &&
      !R.isEmpty(roiTableRows) &&
      !R.isEmpty(responseCurvesRows) &&
      !R.isEmpty(adstockRows) &&
      !R.isEmpty(attributionByGeoRows) &&
      !R.isEmpty(R.keys(channelBreakoutMap)) &&
      !R.isEmpty(R.keys(geoNames)) &&
      responseCurveDivisor !== undefined &&
      modelRunDate !== undefined
    ) {
      setIsLoading(false);
    }
  }, [
    summaryMetricsRows,
    mapeByGeoRows,
    fitRows,
    mediaSpendRows,
    attributionOverTimeRows,
    roiTableRows,
    responseCurvesRows,
    adstockRows,
    attributionByGeoRows,
    channelBreakoutMap,
    geoNames,
    outcomeVariable,
    responseCurveDivisor,
    modelRunDate
  ]);

  const toggleGeoMMMRun = useCallback(
    (modelRun: GeoMMMInfoRow) => {
      (async () => {
        try {
          setIsLoading(true);
          const newModelRun = R.clone(modelRun);
          newModelRun.clientFacing = !newModelRun.clientFacing;
          await MetricsLambdaFetch('/updateGeoMMMModel', { method: 'POST', body: newModelRun });
          const isSameRun = (model: GeoMMMInfoRow) =>
            model.kpi === newModelRun.kpi &&
            model.outcome === newModelRun.outcome &&
            model.modelRunDate === newModelRun.modelRunDate;
          // Toggling another run from the run menu mustn't change the selection.
          setCurrentModelRun(curr => (curr && isSameRun(curr) ? newModelRun : curr));
          setModelRunOptions(prev =>
            R.map(
              model => (isSameRun(model) ? newModelRun : model),
              R.defaultTo([], prev) as GeoMMMInfoRow[]
            )
          );
          setError({ intent: Intent.SUCCESS, message: 'Model permissions updated successfully.' });
        } catch (e) {
          const reportError = e as Error;
          setError({
            message: reportError.message,
            reportError
          });
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [setError]
  );

  // The run selector (and, on Results, the Group By control) lives in a sub-header row, as on the
  // DTC / Commerce MMM pages; the dark header row (title + tab navs) has no room for them. The same
  // selector serves the Results and Scenario Planning tabs.
  const runSubHeader = useMemo(() => {
    // Stays mounted while loading so the selector keeps its open groups after a run toggle.
    const onResults = tab === TabKey.RESULTS;
    if (!onResults && tab !== TabKey.SCENARIO_PLANNING) return undefined;
    if (onResults && !groupByOptions) return undefined;
    return (
      <div className='geo-mmm-sub-header'>
        {currentModelRun && (
          <MmmRunSelector
            kpiRuns={selectorKpiRuns}
            kpi={geoKpiKey(currentModelRun)}
            selectedDate={currentModelRun.modelRunDate}
            isInternal={isInternal}
            onChange={({ kpi: kpiKey, run }) => {
              const newModelRun = modelRunOptions?.find(
                model => geoKpiKey(model) === kpiKey && model.modelRunDate === run.refreshDate
              );
              if (newModelRun) {
                if (onResults) {
                  setIsLoading(true);
                }
                setCurrentModelRun(newModelRun);
              }
            }}
            onToggleClientFacing={run => {
              const modelRun = modelRunOptions?.find(
                model => geoKpiKey(model) === run.kpi && model.modelRunDate === run.refreshDate
              );
              if (modelRun) {
                toggleGeoMMMRun(modelRun);
              }
            }}
          />
        )}
        {onResults && (
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Group By'
            stretch={StretchMode.FIT}
            onChange={setGroupBy}
            options={groupByOptions}
            value={groupBy}
          />
        )}
      </div>
    );
  }, [
    currentModelRun,
    groupBy,
    groupByOptions,
    isInternal,
    selectorKpiRuns,
    modelRunOptions,
    tab,
    toggleGeoMMMRun
  ]);

  // TODO: remove this after the demo on 10/28.
  let geoNamesToUse = geoNames;
  if (company === DMA_MAP_DEMO_COMPANY) {
    geoNamesToUse = DEMO_DMAS_TO_INCLUDE;
  }

  return (
    <Page
      title='Geo MMM'
      pageType='Geo MMM'
      navs={navs}
      onNav={nav => goToTab(nav as TabKey)}
      selectedNav={tab}
      subHeader={runSubHeader}
    >
      <div className='full-page-router geo-mmm-page'>
        {tab === TabKey.RESULTS && (
          <Results
            groupBy={groupBy}
            outcomeVariable={outcomeVariable}
            isLoading={isLoading}
            summaryMetricsRows={summaryMetricsRows}
            mapeByGeoRows={mapeByGeoRows}
            fitRows={fitRows}
            mediaSpendRows={mediaSpendRows}
            channelBreakoutMap={channelBreakoutMap}
            geoNames={geoNamesToUse}
            attributionOverTimeRows={attributionOverTimeRows}
            roiTableRows={roiTableRows}
            responseCurvesRows={responseCurvesRows}
            adstockRows={adstockRows}
            attributionByGeoRows={attributionByGeoRows}
            responseCurveDivisor={responseCurveDivisor}
            lastRefreshDate={modelRunDate}
          />
        )}
        {tab === TabKey.SCENARIO_PLANNING && sharedRun && (
          <ScenarioPlanning
            modelType='GeoMMM'
            featureMapping={featureMapping}
            sharedRun={sharedRun}
            runModels={runModels}
          />
        )}
        {tab === TabKey.FAQ && <FAQ geoMMM showTechnicalDocumentation={false} />}
      </div>
    </Page>
  );
};
